'use client';

import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { env } from 'next-runtime-env';
import { Permission } from 'types/permissions';
import { useToggle } from 'usehooks-ts';

import { useIsBlocked } from '@/app/components/NavigationBlock';
import Avatar from '@/app/components/ui/Avatar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/Popover';
import UnsavedChangesWarning from '@/app/components/UnsavedChangesWarning';
import { useMe } from '@/app/contexts/MeProvider';

type UserMenuProps = { readonly initials: string };

export default function UserMenu({ initials }: UserMenuProps) {
  const t = useTranslations();
  const { isBlocked } = useIsBlocked();
  const [isOpen, toggleOpen] = useToggle();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const me = useMe();

  const logout = async () => {
    // Clear the next-auth session first
    await signOut({ redirect: false });

    // Redirect to MOI logout to clear MOI's session cookie.
    const callbackUrl = `${pathname}?${searchParams.toString()}`;
    const signinUrl = `${window.location.origin}/signin?callbackUrl=${encodeURIComponent(callbackUrl)}`;
    const moiLogoutUrl = new URL(
      '/ohub/authentication/logout',
      env('NEXT_PUBLIC_MOI_SERVICE_URL'),
    );
    moiLogoutUrl.searchParams.set('redirect_uri', signinUrl);
    window.location.href = moiLogoutUrl.toString();
  };

  const handleClickLogout = () => {
    if (isBlocked) {
      toggleOpen();
    } else {
      void logout();
    }
  };

  const canManageUsers =
    me.permissions.includes(Permission.USER_VIEW_GLOBAL) ||
    me.permissions.includes(Permission.USER_VIEW_CUSTOMER);

  return (
    <>
      <Popover>
        <PopoverTrigger>
          <Avatar>{initials}</Avatar>
        </PopoverTrigger>
        <PopoverContent align="end" sideOffset={12} className="p-0">
          {canManageUsers ? (
            <Link
              href="/users"
              className="block w-full p-4 text-left hover:bg-gray-5"
            >
              {t('navigation_user_management_button')}
            </Link>
          ) : null}
          <button
            className="w-full p-4 text-left text-living-coral-700 hover:bg-gray-5"
            onClick={handleClickLogout}
          >
            {t('navigation_logout_button')}
          </button>
        </PopoverContent>
      </Popover>

      <UnsavedChangesWarning
        open={isOpen}
        onConfirm={logout}
        onCancel={toggleOpen}
      />
    </>
  );
}
